<template>
  <div class="modal fade" id="highlight-detail-dialog" tabindex="-1" aria-labelledby="highlightDetailLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body pt-0">
          <div class="text-center">
            <div class="highlight-icon-wrapper mb-3">
              <img :src="iconUrl" :alt="highlight?.type" class="highlight-icon" />
            </div>
            
            <h4 class="mb-1">{{ highlight?.title || 'Custom POI' }}</h4>
            
            <p class="text-muted mb-3">
              <small>{{ prettyTypeName }}</small>
              <span v-if="highlight?.dayNumber" class="ms-2">• Day {{ highlight.dayNumber }}</span>
            </p>
            
            <p v-if="highlight?.description" class="mb-3">
              {{ highlight.description }}
            </p>
            
            <!-- Image Gallery -->
            <div v-if="hasImages" class="mb-4">
              <div v-if="images.length === 1" class="single-image-container">
                <img 
                  :src="images[0].file?.lgUrl || images[0].url" 
                  :alt="highlight?.title"
                  class="img-fluid rounded highlight-image"
                  @click="openLightbox(0)"
                  style="cursor: pointer; max-height: 400px; width: auto; margin: 0 auto;"
                />
              </div>
              <div v-else class="image-gallery">
                <div class="row g-2">
                  <div 
                    v-for="(image, index) in images.slice(0, 4)" 
                    :key="index"
                    :class="images.length === 2 ? 'col-6' : images.length === 3 && index === 0 ? 'col-12' : 'col-6'"
                  >
                    <div class="position-relative">
                      <img 
                        :src="image.file?.mdUrl || image.file?.lgUrl || image.url"
                        :alt="`${highlight?.title} - Image ${index + 1}`"
                        class="img-fluid rounded highlight-image"
                        @click="openLightbox(index)"
                        style="cursor: pointer; width: 100%; height: 200px; object-fit: cover;"
                      />
                      <div 
                        v-if="images.length > 4 && index === 3" 
                        class="overlay-more"
                        @click="openLightbox(3)"
                      >
                        <span class="text-white fs-4">+{{ images.length - 4 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Audio -->
            <div v-if="audioTracks.length > 0" class="mb-4 text-start">
              <div
                v-for="track in audioTracks"
                :key="track.clientId"
                class="mb-2"
              >
                <div v-if="track.title" class="small text-muted mb-1">
                  <i class="bi bi-music-note-beamed me-1"></i>{{ track.title }}
                </div>
                <audio :src="track.url" controls preload="none" class="w-100"></audio>
              </div>
            </div>

            <div class="d-flex gap-2 justify-content-center">
              <button
                v-if="showAddControlPoint"
                type="button" 
                class="btn btn-primary btn-sm"
                @click="addControlPoint"
              >
                <i class="bi bi-geo-alt me-1"></i>
                Add control point
              </button>
              <button 
                type="button" 
                class="btn btn-outline-primary btn-sm"
                @click="editHighlight"
              >
                <i class="bi bi-pencil me-1"></i>
                Edit
              </button>
              <button 
                type="button" 
                class="btn btn-outline-danger btn-sm"
                @click="deleteHighlight"
              >
                <i class="bi bi-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';
import { POIUtility } from '../../../utils/models/POI';

declare global {
  interface Window {
    openHighlightEditDialog?: (highlight: any) => void;
  }
}

export default defineComponent({
  name: 'HighlightDetailDialog',
  setup() {
    const store = useStore();
    const highlight = ref<any>(null);

    const cleanupOrphanedBackdrops = () => {
      const openModals = document.querySelectorAll('.modal.show');
      const backdrops = document.querySelectorAll('.modal-backdrop');

      backdrops.forEach((backdrop, index) => {
        if (index >= openModals.length) {
          backdrop.remove();
        }
      });

      if (openModals.length === 0 && backdrops.length > 0) {
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
      }
    };

    const prettyTypeName = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        const poiUtil = POIUtility.fromString(highlight.value.type);
        return poiUtil.prettyName();
      } catch (e) {
        return highlight.value.type;
      }
    });
    
    const iconUrl = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        const poiUtil = POIUtility.fromString(highlight.value.type);
        return poiUtil.getPublicImageIcon();
      } catch (e) {
        // Return a default icon
        return POIUtility.fromString('other').getPublicImageIcon();
      }
    });
    
    const showAddControlPoint = computed(() => {
      // Check if planTrail store is available
      return store.state.planTrail !== undefined;
    });
    
    const images = computed(() => {
      if (!highlight.value) return [];

      const previewImages = (highlight.value.imagePreviewUrls || []).map((url: string) => ({
        file: {
          lgUrl: url
        },
        url
      }));

      const existingImages = (highlight.value.images || []).filter((image: any) => !image._destroy);

      if (existingImages.length > 0) {
        return [...existingImages, ...previewImages];
      }

      if (previewImages.length > 0) {
        return previewImages;
      }

      return [];
    });
    
    const hasImages = computed(() => images.value.length > 0);

    // Audio tracks: persisted media (url) plus media uploaded this session but
    // not yet saved (previewUrl). Only entries with a playable URL are shown.
    const audioTracks = computed(() => {
      if (!highlight.value) return [];

      const existing = (highlight.value.media || [])
        .filter((item: any) => item && !item._destroy && item.url)
        .map((item: any) => ({
          clientId: item.clientId,
          url: item.url,
          title: item.title
        }));

      const pending = (highlight.value.mediaSignedIds || [])
        .filter((item: any) => item && item.previewUrl)
        .map((item: any) => ({
          clientId: item.clientId,
          url: item.previewUrl,
          title: item.title
        }));

      return [...existing, ...pending];
    });
    
    const openLightbox = async (startIndex: number) => {
      // Wait for next tick to ensure DOM is updated
      await nextTick();
      
      // Create a temporary container for the lightbox
      const lightboxContainer = document.createElement('div');
      lightboxContainer.className = 'highlight-lightbox';
      lightboxContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      `;
      
      let currentIndex = startIndex;
      
      const updateImage = () => {
        const img = images.value[currentIndex];
        const imgUrl = img.file?.lgUrl || img.url;
        lightboxContainer.innerHTML = `
          <div style="position: relative; max-width: 90%; max-height: 90%;">
            <img src="${imgUrl}" style="max-width: 100%; max-height: 90vh; object-fit: contain;" />
            ${images.value.length > 1 ? `
              <button class="btn btn-light position-absolute" style="top: 50%; left: 10px; transform: translateY(-50%);">&lt;</button>
              <button class="btn btn-light position-absolute" style="top: 50%; right: 10px; transform: translateY(-50%);">&gt;</button>
              <div class="position-absolute text-white" style="bottom: 10px; left: 50%; transform: translateX(-50%);">
                ${currentIndex + 1} / ${images.value.length}
              </div>
            ` : ''}
            <button class="btn-close btn-close-white position-absolute" style="top: 10px; right: 10px;"></button>
          </div>
        `;
        
        // Add event listeners
        const closeBtn = lightboxContainer.querySelector('.btn-close');
        const prevBtn = lightboxContainer.querySelector('button:first-of-type');
        const nextBtn = lightboxContainer.querySelector('button:nth-of-type(2)');
        
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            document.body.removeChild(lightboxContainer);
          });
        }
        
        if (prevBtn && images.value.length > 1) {
          prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = (currentIndex - 1 + images.value.length) % images.value.length;
            updateImage();
          });
        }
        
        if (nextBtn && images.value.length > 1) {
          nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = (currentIndex + 1) % images.value.length;
            updateImage();
          });
        }
      };
      
      updateImage();
      
      // Close on background click
      lightboxContainer.addEventListener('click', (e) => {
        if (e.target === lightboxContainer) {
          document.body.removeChild(lightboxContainer);
        }
      });
      
      // Add keyboard navigation
      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          document.body.removeChild(lightboxContainer);
          document.removeEventListener('keydown', handleKeydown);
        } else if (e.key === 'ArrowLeft' && images.value.length > 1) {
          currentIndex = (currentIndex - 1 + images.value.length) % images.value.length;
          updateImage();
        } else if (e.key === 'ArrowRight' && images.value.length > 1) {
          currentIndex = (currentIndex + 1) % images.value.length;
          updateImage();
        }
      };
      
      document.addEventListener('keydown', handleKeydown);
      document.body.appendChild(lightboxContainer);
    };
    
    const showHighlight = (highlightData: any) => {
      let resolvedHighlight = highlightData;
      if (highlightData?.clientId && store.state.planTrail?.hikeHighlights) {
        const storedHighlight = store.state.planTrail.hikeHighlights.find(
          (item: any) => item.clientId === highlightData.clientId
        );

        if (storedHighlight) {
          const storedCopy = JSON.parse(JSON.stringify(storedHighlight));
          resolvedHighlight = {
            ...storedCopy,
            ...highlightData
          };
        }
      }

      cleanupOrphanedBackdrops();

      highlight.value = resolvedHighlight;
      const modalElement = document.getElementById('highlight-detail-dialog');
      if (modalElement) {
        const modal = Modal.getOrCreateInstance(modalElement);
        modal.show();
      }
    };

    const editHighlight = () => {
      // Close this modal
      const modalElement = document.getElementById('highlight-detail-dialog');
      if (modalElement) {
        const modal = Modal.getInstance(modalElement);
        const highlightCopy = highlight.value ? JSON.parse(JSON.stringify(highlight.value)) : null;

        if (highlightCopy && window.openHighlightEditDialog) {
          const openEditor = () => {
            window.openHighlightEditDialog?.(highlightCopy);
            modalElement.removeEventListener('hidden.bs.modal', openEditor);
          };
          modalElement.addEventListener('hidden.bs.modal', openEditor, { once: true });
        }

        modal?.hide();
        return;
      }

      if (highlight.value && window.openHighlightEditDialog) {
        window.openHighlightEditDialog?.(JSON.parse(JSON.stringify(highlight.value)));
      }
    };
    
    const addControlPoint = async () => {
      if (!highlight.value?.coordinates) return;
      
      // Close the modal first
      const modalElement = document.getElementById('highlight-detail-dialog');
      if (modalElement) {
        const modal = Modal.getInstance(modalElement);
        modal?.hide();
      }
      
      // Add control point at highlight location
      await store.dispatch('planTrail/addMarkerAtCoordinates', {
        lng: highlight.value.coordinates[0],
        lat: highlight.value.coordinates[1]
      });
    };
    
    const deleteHighlight = async () => {
      if (confirm('Are you sure you want to delete this POI?')) {
        const clientId = highlight.value.clientId;
        
        // Remove from map first
        await store.dispatch('planTrail/removeHighlightFromMap', clientId);
        
        // Then remove from state
        store.commit('planTrail/removeHikeHighlight', clientId);
        
        // Close modal
        const modalElement = document.getElementById('highlight-detail-dialog');
        if (modalElement) {
          const modal = Modal.getInstance(modalElement);
          modal?.hide();
        }
      }
    };
    
    // Expose showHighlight method to be called from outside
    window.showHighlightDetail = showHighlight;
    
    return {
      highlight,
      prettyTypeName,
      iconUrl,
      showAddControlPoint,
      addControlPoint,
      editHighlight,
      deleteHighlight,
      images,
      hasImages,
      audioTracks,
      openLightbox
    };
  }
});
</script>

<style scoped>
.highlight-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 50%;
}

.highlight-icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.highlight-image {
  transition: transform 0.2s;
}

.highlight-image:hover {
  transform: scale(1.02);
}

.overlay-more {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.25rem;
}

.image-gallery {
  max-width: 500px;
  margin: 0 auto;
}

.single-image-container {
  text-align: center;
}
</style>
